import React, { useEffect, useMemo, useState, useRef } from 'react'
import Table from 'react-bootstrap/Table'
import Form from 'react-bootstrap/Form'
import Spinner from 'react-bootstrap/Spinner'
import swal from 'sweetalert'
import { useNavigate } from 'react-router-dom'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '../firebase'
import MaskedPhone from '../Components/MaskedPhone';
import { ORGANIZATION_NAME } from '../branding';
import { logAdminActivity } from '../utils/activityLogger';

function MediaConsentNewComp() {
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [files, setFiles] = useState([])
  const [mediaType, setMediaType] = useState('image')
  const [videoLinks, setVideoLinks] = useState('')
  const [students, setStudents] = useState([])
  const [search, setSearch] = useState('')
  const lastSearchRef = useRef('')
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [loadingStudents, setLoadingStudents] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [authUser, setAuthUser] = useState(null)
  const [idToken, setIdToken] = useState(null)

  useEffect(() => {
    const auth = getAuth()
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthUser(user || null)
      if (user) {
        try {
          const token = await user.getIdToken()
          setIdToken(token)
        } catch (err) {
          console.error('Failed to get auth token', err)
        }
      } else {
        setIdToken(null)
      }
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const q = query(collection(db, 'users'), where('userStatus', '==', 'Student'))
        const snapshot = await getDocs(q)
        const items = []
        snapshot.forEach((docSnap) => {
          const data = docSnap.data()
          items.push({
            id: docSnap.id,
            userId: data.userId || docSnap.id,
            bio: data.bio || '',
            phoneNumber: data.phoneNumber || ''
          })
        })
        items.sort((a, b) => a.bio.localeCompare(b.bio))
        setStudents(items)
      } catch (err) {
        console.error('Failed to load students', err)
      } finally {
        setLoadingStudents(false)
      }
    }

    fetchStudents()
  }, [])

  useEffect(() => {
    const trimmed = search.trim()
    const timeoutId = setTimeout(() => {
      if (!trimmed) return
      if (trimmed === lastSearchRef.current) return
      lastSearchRef.current = trimmed
      logAdminActivity({
        action: 'Search',
        details: `Media Consent search: "${trimmed}"`,
        meta: { page: 'Media Consent', query: trimmed }
      })
    }, 600)
    return () => clearTimeout(timeoutId)
  }, [search])

  const filteredStudents = useMemo(() => {
    const queryText = search.trim().toLowerCase()
    if (!queryText) return students
    return students.filter((student) => {
      const name = student.bio.toLowerCase()
      const phone = (student.phoneNumber || '').toLowerCase()
      return name.includes(queryText) || phone.includes(queryText)
    })
  }, [students, search])

  const toggleSelection = (userId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }
      return next
    })
  }

  const allFilteredSelected =
    filteredStudents.length > 0 && filteredStudents.every((student) => selectedIds.has(student.userId))

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allFilteredSelected) {
        filteredStudents.forEach((student) => next.delete(student.userId))
      } else {
        filteredStudents.forEach((student) => next.add(student.userId))
      }
      return next
    })
  }

  const handleFilesChange = (event) => {
    const picked = Array.from(event.target.files || [])
    setFiles(picked)
  }

  const generateOtp = (length = 6) => {
    return Math.floor(100000 + Math.random() * 900000).toString().substring(0, length)
  }

  const parseVideoLinks = () => {
    return videoLinks
      .split(/\n|,/)
      .map((link) => link.trim())
      .filter(Boolean)
  }

  const uploadMediaFiles = async (requestId) => {
    const uploads = files.map(async (file) => {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const storagePath = `media-consent/${requestId}/${Date.now()}_${safeName}`
      const storageRef = ref(storage, storagePath)
      await uploadBytes(storageRef, file)
      const url = await getDownloadURL(storageRef)
      return {
        url,
        name: file.name,
        type: file.type,
        size: file.size,
        storagePath,
        source: 'upload'
      }
    })

    return Promise.all(uploads)
  }

  const buildMediaItems = async (requestId) => {
    if (mediaType === 'image') {
      return uploadMediaFiles(requestId)
    }

    const links = parseVideoLinks()
    return links.map((link) => ({
      url: link,
      name: link,
      type: 'video-link',
      source: 'link'
    }))
  }

  const sendConsentSms = async (phoneNumber, message) => {
    if (!idToken) {
      throw new Error('Missing auth token for SMS')
    }

    const response = await fetch('https://us-central1-ubunifu-college.cloudfunctions.net/api/send-sms', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      body: JSON.stringify({ phoneNumber, message })
    })

    const data = await response.json()
    if (data.status !== 222) {
      throw new Error(data.status_message || 'SMS sending failed')
    }

    return data
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!title.trim()) {
      swal('Missing Title', 'Please enter a title for the consent request.', 'error')
      return
    }

    if (!description.trim()) {
      swal('Missing Description', 'Please add a description for the consent request.', 'error')
      return
    }

    if (mediaType === 'image') {
      if (files.length === 0) {
        swal('Missing Media', 'Please upload at least one image.', 'error')
        return
      }
      const invalidFile = files.find((file) => !file.type.startsWith('image/'))
      if (invalidFile) {
        swal('Invalid File', 'Only image files are allowed when Image is selected.', 'error')
        return
      }
    } else {
      const links = parseVideoLinks()
      if (links.length === 0) {
        swal('Missing Video Link', 'Please paste at least one video link.', 'error')
        return
      }
    }

    const selectedStudents = students.filter((student) => selectedIds.has(student.userId))
    if (selectedStudents.length === 0) {
      swal('No Students Selected', 'Please select at least one student.', 'error')
      return
    }

    if (!authUser) {
      swal('Authentication Required', 'Please log in again before creating a request.', 'error')
      return
    }
    if (!idToken) {
      swal('Authentication Required', 'Unable to send SMS without a valid token. Please refresh and try again.', 'error')
      return
    }

    setSubmitting(true)

    try {
      const requestRef = doc(collection(db, 'MediaConsentRequests'))
      const requestId = requestRef.id
      const createdAt = Timestamp.now()

      const media = await buildMediaItems(requestId)

      await setDoc(requestRef, {
        docID: requestId,
        title: title.trim(),
        description: description.trim(),
        mediaType,
        media,
        createdAt,
        createdBy: authUser.uid,
        studentIds: selectedStudents.map((student) => student.userId),
        studentCount: selectedStudents.length,
        students: selectedStudents.map((student) => ({
          studentId: student.userId,
          name: student.bio,
          phoneNumber: student.phoneNumber
        }))
      })

      const baseUrl = 'https://admin.ubunifucollege.com'
      const failures = []

      await Promise.all(
        selectedStudents.map(async (student) => {
          const tokenRef = doc(collection(db, 'MediaConsentTokens'))
          const tokenId = tokenRef.id
          const otpCode = generateOtp()
          const consentLink = `${baseUrl}/media-consent/respond/${tokenId}`
          const message = `${ORGANIZATION_NAME} Media Consent: ${title.trim()}. Link: ${consentLink}. OTP: ${otpCode} (required to approve)`
          await setDoc(tokenRef, {
            token: tokenId,
            requestId,
            studentId: student.userId,
            studentName: student.bio,
            phoneNumber: student.phoneNumber || '',
            otpRequired: !!student.phoneNumber,
            otpCode: student.phoneNumber ? otpCode : null,
            otpVerified: false,
            status: 'pending',
            createdAt,
            respondedAt: null
          })

          if (!student.phoneNumber || student.phoneNumber.length < 8) {
            failures.push({ studentId: student.userId, reason: 'Missing phone number' })
            await updateDoc(tokenRef, { smsStatus: 'skipped', smsError: 'Missing phone number' })
          } else {
            try {
              const smsResult = await sendConsentSms(student.phoneNumber, message)
              await updateDoc(tokenRef, {
                smsStatus: 'sent',
                smsUniqueId: smsResult.unique_id || ''
              })
            } catch (err) {
              failures.push({ studentId: student.userId, reason: err.message })
              await updateDoc(tokenRef, { smsStatus: 'failed', smsError: err.message })
            }
          }

        })
      )

      if (failures.length > 0) {
        swal(
          'Request Created',
          `Consent request created, but ${failures.length} SMS message(s) failed or were skipped.`,
          'warning'
        ).then(() => navigate('/media-consent'))
      } else {
        swal('Success', 'Media consent request created and sent successfully.', 'success').then(() =>
          navigate('/media-consent')
        )
      }
    } catch (err) {
      console.error('Failed to create media consent request', err)
      swal('Error', err.message || 'Failed to create media consent request.', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="clientsComp">
      <div className="dataCompTop">
        <h5>New Media Consent Request</h5>
      </div>

      <form className="media-consent-form" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-lg-6">
            <Form.Group className="mb-3">
              <Form.Label>Title of Request</Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="e.g. Graduation Ceremony Media Consent"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description / Reason for Consent</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                placeholder="Explain why you need consent"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Media Type</Form.Label>
              <div className="media-consent-media-type">
                <Form.Check
                  inline
                  type="radio"
                  name="mediaType"
                  id="mediaTypeImage"
                  label="Image Upload"
                  checked={mediaType === 'image'}
                  onChange={() => {
                    setMediaType('image')
                    setVideoLinks('')
                  }}
                />
                <Form.Check
                  inline
                  type="radio"
                  name="mediaType"
                  id="mediaTypeVideo"
                  label="Video Link"
                  checked={mediaType === 'video'}
                  onChange={() => {
                    setMediaType('video')
                    setFiles([])
                  }}
                />
              </div>
            </Form.Group>

            {mediaType === 'image' && (
              <Form.Group className="mb-3">
                <Form.Label>Upload Photo(s)</Form.Label>
                <Form.Control type="file" multiple accept="image/*" onChange={handleFilesChange} />
                {files.length > 0 && (
                  <ul className="media-consent-file-list">
                    {files.map((file) => (
                      <li key={file.name}>{file.name}</li>
                    ))}
                  </ul>
                )}
              </Form.Group>
            )}

            {mediaType === 'video' && (
              <Form.Group className="mb-3">
                <Form.Label>Video Link(s)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={videoLinks}
                  onChange={(event) => setVideoLinks(event.target.value)}
                  placeholder="Paste one or more video links (one per line)"
                />
              </Form.Group>
            )}

            <div className="media-consent-actions">
              <button className="btn btn-primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <span className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    Creating...
                  </span>
                ) : (
                  'Create Request'
                )}
              </button>
              <button className="btn btn-outline-secondary" type="button" onClick={() => navigate('/media-consent')}>
                Cancel
              </button>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="media-consent-students">
              <div className="media-consent-students-header">
                <h6>
                  Select Students <span className="badge text-bg-success">{selectedIds.size}</span>
                </h6>
                <div className="media-consent-students-search">
                  <input
                    type="text"
                    placeholder="Search by name or phone"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>
              </div>

              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>
                        <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} />
                      </th>
                      <th>Name</th>
                      <th>Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingStudents && (
                      <tr>
                        <td colSpan="3" className="text-center">Loading students...</td>
                      </tr>
                    )}
                    {!loadingStudents && filteredStudents.length === 0 && (
                      <tr>
                        <td colSpan="3" className="text-center">No students found</td>
                      </tr>
                    )}
                    {filteredStudents.map((student) => (
                      <tr key={student.userId}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(student.userId)}
                            onChange={() => toggleSelection(student.userId)}
                          />
                        </td>
                        <td>{student.bio}</td>
                        <td>
                          <MaskedPhone
                            phoneNumber={student.phoneNumber}
                            studentId={student.userId}
                            studentName={student.bio}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export default MediaConsentNewComp
